/*
 * -----------------------------------------------------------------------
 * <copyright file="helpers.ts" company="Triumph Research Intelligence">
 * Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 * </copyright>
 * -----------------------------------------------------------------------
 *
 */

import moment from 'moment';
import { parseExcelDate } from 'read-excel-file/browser';
import { NamedItemType, RiskStatusType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { NamedItemFields } from '@queries/generated/fragments.generated.ts';

const checkData = (data: [string], errors: any): boolean => {
    if (!data || !data.length) {
        errors.push({ message: 'Error reading file, no data found.' });
        return false;
    }

    return true;
};

const checkLine = (line: [string], lineNumber: number, errors: any): boolean => {
    if (!line) {
        if (!errors) errors = [];
        errors.push({ line: lineNumber + 1, message: 'No line found' });
        return false;
    }

    if (line.length < 1) {
        if (!errors) errors = [];
        errors.push({ line: lineNumber + 1, message: 'No values found.' });
        return false;
    }

    // Skip empty lines (need to handle null values from Excel)
    return !(!line.length || (line.length === 1 && (!line[0] || (line[0] && !line[0].toString().trim().length))));
};

/** * currently we are only allowing "," & "|" separators ** */
const splitString = (
    name: string,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    separator: string,
    required: boolean = false,
): string[] | null => {
    const val = line.length > index && line[index] ? line[index].toString().trim() : null;
    if (val == null) {
        return null;
    }

    if (separator !== ',' && separator !== '|') {
        errors.push({
            line: lineNumber,
            message: `Invalid separator not found: ${name}, only ',' and '|' separators are allowed.`,
        });
        return null;
    }

    if (!val?.length && required) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }
    return val ? val.replace('�', ' ').split(separator) : val.split(separator);
};

const readString = (
    name: string,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    maxLength: number = 0,
    required: boolean = false,
    defaultValue: string | null = '',
): string | null => {
    const val = line.length > index && line[index] ? line[index].toString().trim() : null;
    if (!val?.length && required) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (maxLength && val && val.length > maxLength) {
        errors.push({ line: lineNumber, message: `${name} exceeds maximum of ${maxLength} characters` });
        return null;
    }

    return val ? val.replace('�', ' ') : defaultValue;
};

const readBool = (
    name: string,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    required: boolean = false,
): boolean | null => {
    if (required && (!line[index] || line.length <= index || !line[index].toString().trim().length)) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (line.length > index && line[index] && line[index].toString().trim().length) {
        const b = line[index].toString().trim().toUpperCase()[0];
        if (b === 'Y') return true;
        if (b === 'N') return false;

        errors.push({ line: lineNumber, message: `Invalid yes/no value: ${name}` });
    }

    return null;
};

const readDate = (
    name: string,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    required: boolean = false,
): string | null => {
    if (required && (!line[index] || line.length <= index || !line[index].toString().trim().length)) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    const isValidDate = (date: string) => {
        const regEx = /^\d{4}-\d{2}-\d{2}$/;
        if (!date.match(regEx)) return false;

        const d = new Date(date);
        const dNum = d.getTime();
        if (!dNum && dNum !== 0) return false;

        return d.toISOString().slice(0, 10) === date;
    };

    if (line.length > index && line[index] && line[index].toString().trim().length) {
        try {
            // If it's a number, then it probably came from Excel, convert it to a date
            const val =
                typeof line[index] === 'number'
                    ? moment(parseExcelDate(line[index]) as unknown as Date).format('YYYY-MM-DD')
                    : line[index].toString().trim();

            if (!isValidDate(val)) errors.push({ line: lineNumber, message: `Invalid date value: ${name} (${val})` });
            else return val;
        } catch (ex) {
            errors.push({ line: lineNumber, message: `Invalid date value: ${name} (${ex})` });
        }
    }

    return null;
};

const readEnum = (
    name: string,
    values: any,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    required: boolean = false,
) => {
    if (required && (!line[index] || line.length <= index || !line[index].toString().trim().length)) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (values && line.length > index && line[index] && line[index].toString().trim().length) {
        const val = line[index].toString().trim().toUpperCase();
        if (values.indexOf(val) < 0) errors.push({ line: lineNumber, message: `Invalid value: ${name} (${val})` });
        else return val;
    }

    return null;
};

const readRiskStatus = (
    name: string,
    statuses: any,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    required: boolean = false,
) => {
    const statusValueExists = line.length > index && line[index] && line[index].toString().trim().length;
    if (required && !statusValueExists) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (statusValueExists) {
        try {
            const selectedStatus = statuses?.find(
                (status: any) => status.text.toUpperCase().trim() === line[index].toString().trim().toUpperCase(),
            );

            if (!selectedStatus)
                errors.push({ line: lineNumber, message: `Invalid Status value: ${name} (${line[index]})` });
            else return selectedStatus.id;
        } catch (ex) {
            errors.push({ line: lineNumber, message: `Error getting Status value: ${name} - ${ex}` });
        }
    }

    return RiskStatusType.IDENTIFIED;
};

const readNumber = (
    name: string,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    required: boolean = false,
): number | null => {
    if (
        required &&
        (line[index] === null ||
            line[index] === undefined ||
            line.length <= index ||
            !line[index].toString().trim().length)
    ) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (
        line.length > index &&
        line[index] !== null &&
        line[index] !== undefined &&
        line[index].toString().trim().length
    ) {
        const value = parseInt(line[index], 10);

        if (Number.isNaN(value)) {
            errors.push({ line: lineNumber, message: `Invalid numeric value: ${name} (${line[index]})` });
            return null;
        }
        return value;
    }

    return null;
};

const readStudyResponsibleFunctions = (
    name: string,
    lovs: NamedItemFields[],
    type: NamedItemType.RESPONSIBLE_FUNCTION,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    studyId: string,
    required: boolean = false,
) => {
    if (required && (!line[index] || line.length <= index || !line[index].toString().trim().length)) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (line.length > index && line[index] && line[index].toString().trim().length) {
        try {
            const selectedResponsibleFunction = lovs?.find(
                (lov: NamedItemFields) =>
                    lov.studyId === studyId &&
                    lov.type === type &&
                    lov.name?.toUpperCase().trim() === line[index].toString().trim().toUpperCase(),
            );

            if (!selectedResponsibleFunction)
                errors.push({ line: lineNumber, message: `Invalid Study Function value: ${name} (${line[index]})` });
            else if (!selectedResponsibleFunction.active)
                errors.push({ line: lineNumber, message: `Inactive Study Function value: ${name} (${line[index]})` });
            else return selectedResponsibleFunction.id;
        } catch (ex) {
            errors.push({ line: lineNumber, message: `Error getting Study Function value: ${name} - ${ex}` });
        }
    }

    return Constants.ObjectId.Empty;
};

const readLov = (
    name: string,
    lovs: any,
    type: NamedItemType,
    line: [string],
    index: number,
    lineNumber: number,
    errors: any,
    required: boolean = false,
) => {
    if (required && (!line[index] || line.length <= index || !line[index].toString().trim().length)) {
        errors.push({ line: lineNumber, message: `Required value not found: ${name}` });
        return null;
    }

    if (line.length > index && line[index] && line[index].toString().trim().length) {
        try {
            const selectedLov = lovs?.find(
                (lov: any) =>
                    lov.type === type && lov.name.toUpperCase().trim() === line[index].toString().trim().toUpperCase(),
            );

            if (!selectedLov) errors.push({ line: lineNumber, message: `Invalid LOV value: ${name} (${line[index]})` });
            else if (!selectedLov.active)
                errors.push({ line: lineNumber, message: `Inactive LOV value: ${name} (${line[index]})` });
            else return selectedLov.id;
        } catch (ex) {
            errors.push({ line: lineNumber, message: `Error getting LOV value: ${name} - ${ex}` });
        }
    }

    return Constants.ObjectId.Empty;
};

export {
    checkData,
    checkLine,
    readString,
    splitString,
    readBool,
    readDate,
    readEnum,
    readNumber,
    readLov,
    readRiskStatus,
    readStudyResponsibleFunctions,
};
